import { useParams, useLocation, Navigate, Route, Routes } from "react-router";
import Segments from "components/navigation/segments/Segments";
import { bindAction } from "interfaces";
import { withLDConsumer } from "launchdarkly-react-client-sdk";
import { useEffect, useState, useMemo, useCallback } from "react";
import { connect, type ConnectedProps } from "react-redux";
import { bindActionCreators } from "redux";
import { Dimmer, Loader, Segment } from "semantic-ui-react";
import { RestrictedResource } from "components";
import GenericSectionHeader from "components/genericSectionHeader/GenericSectionHeader";
import { RolePermissions, RouteNames, SortOptions, ViewType } from "enums";
import { useBackNavigation } from "utils/navigationUtils";
import * as backgroundTasksActions from "features/BackgroundTasks/state/backgroundTasksActions";
import {
  accountPacksSearchSelector,
  setAccountPacksSearchTerm,
} from "features/Licensing/Packs/state/slices/accountPacksSearchSlice";
import { resetAccountPacksGrid } from "features/Accounts/state/slices/accountPacksSlice";
import { fetchAccountPacksGrid } from "features/Accounts/state/thunks/accountPacksThunk";
import { type GetPacksRequest, type PacksRequest } from "features/Licensing/Packs/types/requests";
import * as notificationsActions from "features/Notifications/state/notificationsActions";
import * as accountInformationActions from "features/Accounts/AccountInformation/state/accountInformationActions";
import * as editAccountActions from "features/Accounts/EditAccount/state/editAccountActions";
import { permissionsToAccountSelector } from "features/Accounts/EditAccount/state/slices/permissionsToAccountSlice";
import { fetchPermissionsToAccount } from "features/Accounts/EditAccount/state/thunks/permissionsToAccountThunk";
import {
  selectHasTemplatesTabAccess,
  selectHasTemplateUsersTabAccess,
} from "features/Accounts/state/selectors/accountSelectors";
import { pluralize } from "utils/stringUtils";
import { DeleteLinkButton } from "components/buttons/linkButtons";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { type LDProps } from "features/LDProps";
import { reset as resetParents } from "features/Accounts/state/slices/parentAccountSlice";
import styles from "../styles/EditAccount.module.scss";
import {
  setSelected as setSelectedUsers,
  reset as resetTemplateUsersAction,
} from "features/Accounts/state/slices/editAccountUsersSlice";
import { fetchAccountUsers } from "features/Accounts/state/thunks/editAccountUsersThunk";
import EditAccountUsersList from "features/Accounts/EditAccount/TemplateUsers/EditAccountUsersList";
import { fetchAccountsTags as fetchAccountsTagsAction } from "features/Accounts/state/thunks/accountsLabelsThunk";
import { setNewTag as setNewTagAction } from "features/Accounts/state/slices/tagsSlice";

import { EditLimitedInfo } from "features/Accounts/EditAccount/EditLimitedInfo/EditLimitedInfo";
import {
  fetchAccountPacks,
  resetAccountPacksForbidden,
  resetAccountPacksList,
  updateAccountInfo,
} from "features/Accounts/EditAccount/state/editAccountActions";
import {
  fetchLimitedAccountInfo,
  updateLimitedAccountInfo as updateLimitedAccountInfoThunk,
} from "features/Accounts/EditAccount/state/thunks/limitedAccountInfoThunk";
import * as React from "react";
import EditTemplates from "features/Accounts/EditAccount/Templates/EditTemplates";
import { BasePacksTable } from "features/Accounts/EditAccount/EditPacks/BasePacksTable/BasePacksTable";
import cn from "classnames";
import { useObservable } from "hooks/useObservable";
import { setLimitedAccountInfo as setLimitedAccountInfoAction } from "features/Accounts/EditAccount/state/slices/limitedAccountInfoSlice";
import { AccountTypes } from "features/Accounts/types";
import { getPurchasePacksAddonEnabled } from "features/Accounts/state/selectors/addonsSelector";
import { AddTemplateUserButton } from "features/Accounts/TemplateUsers/AddTemplateUserButton";

const overviewUrl = `/${RouteNames.linkedAccountsManagement}`;
const limitedPath = `/${RouteNames.limitedAccountsManagement}`;
const viewOnlyPath = `/${RouteNames.viewOnlyAccountsManagement}`;

const checkAccountType = (state: RootState, accountTypeId: number) => state.userProfile.accountTypeId === accountTypeId;

const segmentPaths = {
  content: "content",
  packs: "packs",
  templateUsers: "template-users",
  templates: "templates",
};

enum Tabs {
  Configuration = "Configuration",
  Users = "Template Users",
  Templates = "Templates",
  Packs = "Packs",
}

const viewOnlyTabs = new Set([Tabs.Configuration, Tabs.Packs]);

type Props = LimitedAccountPropsFromRedux &
  LDProps & {
    viewOnly?: boolean;
  };

const EditLimitedAccount: React.FC<Props> = (props) => {
  const params = useParams();
  const location = useLocation();
  const {
    currentAccountId,
    editAccountActions,
    fetchPermissionsToAccount,
    tags,
    fetchAccountsTags,
    selectedUsers,
    setSelectedUsers,
    resetState,
    accountConfiguration,
    isLoadingPermissionsToAccount,
    isLoading,
    packsList,
    packsGrid,
    isMobo,
    hasGlobalAccess,
    accountPacksActions,
    accountPacksSearch,
    userPermissions,
    areAllLoaded,
    setNewTag,
    isPacksAccessAuthorized,
    updateLimitedAccountInfo,
    setLimitedAccountInfo,
    hasPurchasePackAddon,
    viewOnly,
  } = props;

  const [selectedViewType, setSelectedViewType] = useState(ViewType.LIST);

  const userObserver = useObservable();
  const userImportObserver = useObservable();
  const usersDeleteObserver = useObservable<(onConfirm: () => void, ids: number[]) => void>();

  const back = useBackNavigation(overviewUrl);

  const accountId = useMemo(() => {
    return parseInt(params?.moboId as string) || currentAccountId;
  }, [currentAccountId, params?.moboId]);

  const isUsersTab = useMemo(() => {
    return location.pathname.includes(segmentPaths.templateUsers);
  }, [location.pathname]);

  const isOwnAccount = !isMobo && currentAccountId === accountId;
  const isInfoLoading = isLoading || isLoadingPermissionsToAccount;
  const basePath = viewOnly ? viewOnlyPath : limitedPath;

  useEffect(() => {
    if (!tags.length) {
      fetchAccountsTags(false);
    }
  }, [fetchAccountsTags, tags.length]);

  useEffect(() => {
    editAccountActions.fetchLimitedAccountInfo(accountId);
    fetchPermissionsToAccount(currentAccountId);

    return () => {
      editAccountActions.resetAccountPacksForbidden();
      resetState();
    };
  }, [accountId, currentAccountId, editAccountActions, fetchPermissionsToAccount, resetState]);

  const renderAddUserButton = () => {
    return (
      <AddTemplateUserButton
        shouldRender={selectedUsers.length === 0}
        onImportUsers={userImportObserver.notify}
        onCreateUser={userObserver.notify}
      />
    );
  };

  const renderDeleteUserButton = () => {
    return (
      <DeleteLinkButton
        onClick={() => {
          if (selectedUsers.length === 0) {
            return;
          }
          usersDeleteObserver.notify(() => {
            setSelectedUsers([]);
          }, selectedUsers);
        }}
      />
    );
  };

  const renderHeaderButtons = () => {
    const buttons: JSX.Element[] = [];

    const addButton = (condition: boolean, renderFn: () => JSX.Element) => {
      if (condition) {
        buttons.push(renderFn());
      }
    };

    addButton(isUsersTab, renderAddUserButton);

    addButton(isUsersTab && selectedUsers.length > 0, renderDeleteUserButton);

    return buttons;
  };

  const refreshPacksList = (accountId: number, selectedViewType: ViewType) => {
    if (selectedViewType === ViewType.GRID) {
      accountPacksActions.resetAccountPacksGrid();
      accountPacksActions.fetchAccountPacksGrid({
        accountId,
        skip: 0,
        orderBy: SortOptions.Title,
        filters: {},
        searchTerm: accountPacksSearch.term,
      });
    }
  };

  const triggerRefreshPacksGrid = (viewType: ViewType) => {
    refreshPacksList(accountId, viewType);
  };

  const updateAccountInfoHandler = (data: { tags: string[] }) => {
    updateLimitedAccountInfo({ accountId, data });
    setLimitedAccountInfo(data.tags);
  };

  const title = useMemo((): string => {
    if (isUsersTab && selectedUsers.length > 0) {
      return `${selectedUsers.length} ${pluralize("User", selectedUsers.length)} Selected`;
    }

    return accountConfiguration.name;
  }, [accountConfiguration.name, isUsersTab, selectedUsers]);

  const titleClassName = useMemo(() => {
    if (isUsersTab && selectedUsers.length > 0) {
      return `selected-items-label`;
    }
    return "";
  }, [isUsersTab, selectedUsers]);

  const loadPacksList = (skip: number, top: number, order: string, appliedFilter: any, searchTerm?: string) => {
    const request: GetPacksRequest = {
      skip,
      top,
      orderBy: order,
      searchTerm,
    };
    editAccountActions.fetchAccountPacks(accountId, request, appliedFilter);
  };

  const loadPacksGrid = (skip: number, order: string, appliedFilter: any, searchTerm?: string) => {
    return accountPacksActions.fetchAccountPacksGrid({
      accountId,
      skip,
      orderBy: order,
      filters: appliedFilter,
      searchTerm,
    });
  };

  const checkShouldRenderTemplateUsersTab = () => {
    return !props.hasChannelPartnerAccountType && props.hasTemplateUsersTabAccess;
  };

  const checkShouldRenderTemplatesTab = () => {
    return props.hasTemplatesTabAccess;
  };

  const checkIsReadOnlyMode = useCallback(
    (viewPermission: RolePermissions, managePermission: RolePermissions) => {
      return props.userPermissions.includes(viewPermission) && !props.userPermissions.includes(managePermission);
    },
    [props.userPermissions],
  );

  const shouldRender = (tabName: Tabs, and: boolean = true) => and && (!viewOnly || viewOnlyTabs.has(tabName));

  const tabs = [
    {
      path: null,
      label: Tabs.Configuration,
      shouldRender: shouldRender(Tabs.Configuration),
      element: (
        <RestrictedResource isAuthorized>
          <EditLimitedInfo
            accountId={accountId}
            currentConfiguration={accountConfiguration}
            onInfoUpdated={updateAccountInfoHandler}
            isOwnAccount={isOwnAccount}
            isLoading={isInfoLoading}
            isMobo={isMobo}
            hasGlobalAccess={hasGlobalAccess}
            onAddNewTag={setNewTag}
            tags={tags}
          />
        </RestrictedResource>
      ),
    },
    {
      path: segmentPaths.templateUsers,
      label: Tabs.Users,
      shouldRender: shouldRender(Tabs.Users, checkShouldRenderTemplateUsersTab()),
      element: (
        <>
          {areAllLoaded ? (
            <EditAccountUsersList
              editAccountId={accountId}
              onImportUsersModalObserver={userImportObserver}
              renderAddUsersButton={renderAddUserButton}
              onAddUserModalObserver={userObserver}
              onDeleteUsersObserver={usersDeleteObserver}
              isReadOnly={checkIsReadOnlyMode(
                RolePermissions.AccountsSettingsView,
                RolePermissions.AccountsSettingsManage,
              )}
            />
          ) : (
            <Loader active />
          )}
        </>
      ),
    },
    {
      path: segmentPaths.templates,
      label: "Templates",
      shouldRender: shouldRender(Tabs.Templates, checkShouldRenderTemplatesTab()),
      element: (
        <RestrictedResource permissions={[RolePermissions.TemplatesView]}>
          <EditTemplates accountId={Number(params.moboId)} title="Templates" />
        </RestrictedResource>
      ),
    },
    {
      path: segmentPaths.packs,
      label: Tabs.Packs,
      shouldRender: shouldRender(Tabs.Packs),

      element: (
        <RestrictedResource isAuthorized={isPacksAccessAuthorized}>
          <>
            {isInfoLoading ? (
              <Dimmer.Dimmable as={Segment} dimmed={isInfoLoading}>
                <Dimmer active={isInfoLoading} inverted>
                  <Loader>Loading</Loader>
                </Dimmer>
              </Dimmer.Dimmable>
            ) : (
              <BasePacksTable
                accountId={accountId}
                packsList={packsList}
                packsGrid={packsGrid}
                loadListItems={loadPacksList}
                loadGridItems={loadPacksGrid}
                resetListItems={editAccountActions.resetAccountPacksList}
                resetGridItems={accountPacksActions.resetAccountPacksGrid}
                hasPurchasePower={userPermissions.includes(RolePermissions.PacksPurchase)}
                searchState={accountPacksSearch}
                setSearch={accountPacksActions.setAccountPacksSearch}
                triggerRefreshPacksGrid={triggerRefreshPacksGrid}
                isAccountHome={false}
                moboAccountId={accountConfiguration.id}
                setSelectedViewType={setSelectedViewType}
                selectedViewType={selectedViewType}
                hasPurchasePackAddon={hasPurchasePackAddon}
              />
            )}
          </>
        </RestrictedResource>
      ),
    },
  ];

  return (
    <section className={cn(styles["edit-account"], "nested-content")}>
      <GenericSectionHeader
        title={title}
        titleForGA="Account Details"
        goBackAction={back}
        showGoBackIcon={selectedUsers.length <= 0}
        buttons={renderHeaderButtons()}
        titleClassname={titleClassName}
      />

      <Segments to={`${basePath}/${accountId}`}>
        {tabs.map((tab, index) => tab.shouldRender && <Segments.Segment to={tab.path} label={tab.label} key={index} />)}
      </Segments>

      <Routes>
        {tabs.map(
          (tab, index) => tab.shouldRender && <Route path={tab.path ?? "/"} element={tab.element} key={index} />,
        )}
        <Route path="*" element={<Navigate to="../" replace />} />
      </Routes>
    </section>
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const { edit, templateUsers } = state.accounts;
  const { userProfile } = state;

  return {
    isPacksAccessAuthorized: edit.accountPacks.isAccessAuthorized,
    currentAccountId: state.userProfile.accountId,
    accountConfiguration: edit.limitedAccountInfo,
    isLoadingPermissionsToAccount: edit.permissionsToAccount.isLoading,
    isLoading: edit.limitedAccountInfo.isLoading,
    error: edit.limitedAccountInfo.error,
    isMobo: userProfile.isMobo,
    hasGlobalAccess: userProfile.hasGlobalAccess,
    hasChannelPartnerAccountType: checkAccountType(state, AccountTypes.ChannelPartner),
    hasTemplateUsersTabAccess: selectHasTemplateUsersTabAccess(state),
    hasTemplatesTabAccess: selectHasTemplatesTabAccess(state),
    hasPurchasePackAddon: getPurchasePacksAddonEnabled(state),
    userPermissions: permissionsToAccountSelector(state),
    areAllLoaded: state.accounts.edit.permissionsToAccount.areAllLoaded,
    packsList: edit.packsList,
    packsGrid: edit.packsGrid,
    accountPacksSearch: accountPacksSearchSelector(state),
    selectedUsers: templateUsers.selected,
    tags: state.accounts.tags.items,
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    editAccountActions: bindActionCreators(
      {
        fetchLimitedAccountInfo,
        resetAccountPacksList,
        fetchAccountPacks,
        resetAccountPacksForbidden,
        updateAccountInfo,
      },
      dispatch,
    ),
    updateData: bindActionCreators(accountInformationActions.updateData, dispatch),
    notificationsActions: bindActionCreators(notificationsActions, dispatch),
    backgroundTasksActions: bindActionCreators(backgroundTasksActions, dispatch),
    accountPacksActions: {
      resetAccountPacksGrid: () => dispatch(resetAccountPacksGrid()),
      fetchAccountPacksGrid: (requestData: PacksRequest) => dispatch(fetchAccountPacksGrid(requestData)),
      setAccountPacksSearch: (term: string) => dispatch(setAccountPacksSearchTerm(term)),
    },
    fetchPermissionsToAccount: bindAction(fetchPermissionsToAccount, dispatch),
    fetchAccountUsers: bindAction(fetchAccountUsers, dispatch),
    setSelectedUsers: bindAction(setSelectedUsers, dispatch),
    resetTemplateUsers: bindAction(resetTemplateUsersAction, dispatch),
    resetState: () => {
      dispatch(resetParents());
      dispatch(editAccountActions.resetEditAccountInfo());
      dispatch(resetTemplateUsersAction());
    },
    fetchAccountsTags: bindAction(fetchAccountsTagsAction, dispatch),
    setNewTag: bindAction(setNewTagAction, dispatch),
    updateLimitedAccountInfo: bindAction(updateLimitedAccountInfoThunk, dispatch),
    setLimitedAccountInfo: bindAction(setLimitedAccountInfoAction, dispatch),
  };
};

/* istanbul ignore next */
const connector = connect(mapStateToProps, mapDispatchToProps);
export type LimitedAccountPropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(withLDConsumer()(EditLimitedAccount));
export default ConnectedComponent;
